import { WEB_MAP_POLICY } from "./web_policy"

export const VECTOR_LAYERS = Object.freeze({
  trails: ["lyr-trails", "lyr-trails-pt"],
  rivers: ["lyr-rivers", "lyr-rivers-pt"],
  usfs: ["lyr-usfs"],
  usgs_trails: ["lyr-usgs-trails"],
  blm: ["lyr-blm"],
  wma: ["lyr-wma-fill"],
  public_lands: ["lyr-public-lands-fill"],
  parcels: ["lyr-parcels-fill"]
})

export const MANAGED_TRAIL_LAYERS = Object.freeze(["trails", "usfs", "usgs_trails", "blm"])

export const MANAGED_TRAIL_SOURCES = Object.freeze({
  trails: "src-trails",
  usfs: "src-usfs",
  usgs_trails: "src-usgs-trails",
  blm: "src-blm"
})

export const PARCEL_LABEL_LIMIT = WEB_MAP_POLICY.parcelLabelLimit

export const INTERACTIVE_LAYERS = Object.freeze(Object.values(VECTOR_LAYERS).flat())

export const GEOJSON_SOURCE_IDS = Object.freeze([
  "src-trails",
  "src-trails-selected",
  "src-rivers",
  "src-usfs",
  "src-usgs-trails",
  "src-blm",
  "src-wma",
  "src-public-lands",
  "src-parcels"
])

export function overlayLayerDefinitions() {
  return [
    { id: "lyr-wma-fill", type: "fill", source: "src-wma",
      paint: { "fill-color": "#9c27b0", "fill-opacity": 0.12 } },
    { id: "lyr-wma-line", type: "line", source: "src-wma",
      paint: { "line-color": "#6a1b9a", "line-width": 2 } },
    { id: "lyr-public-lands-fill", type: "fill", source: "src-public-lands",
      paint: { "fill-color": ["match", ["get", "owner_type"],
        "FED", "#84a98c", "TRIB", "#b08968", "STAT", "#6b8e23",
        "LOC", "#4f86c6", "DIST", "#8b5cf6", "TERR", "#0f766e", "#64748b"],
        "fill-opacity": 0.24 } },
    { id: "lyr-public-lands-line", type: "line", source: "src-public-lands",
      paint: { "line-color": "#365314", "line-width": 1.2, "line-opacity": 0.9 } },
    { id: "lyr-parcels-fill", type: "fill", source: "src-parcels",
      paint: { "fill-color": "#ff6a1a", "fill-opacity": 0.05 } },
    { id: "lyr-parcels-line", type: "line", source: "src-parcels",
      paint: { "line-color": "#ff6a1a", "line-width": 1.25 } },
    { id: "lyr-rivers", type: "line", source: "src-rivers",
      filter: ["!=", ["geometry-type"], "Point"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#0277bd", "line-width": 1.5, "line-opacity": 0.85 } },
    { id: "lyr-usfs", type: "line", source: "src-usfs",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ["case", ["to-boolean", ["get", "motorized"]], "#e65100", "#1b5e20"], "line-width": 3, "line-opacity": 0.85 } },
    { id: "lyr-usgs-trails", type: "line", source: "src-usgs-trails",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ["case", ["to-boolean", ["get", "motorized"]], "#c2410c", "#2f855a"], "line-width": 2.75, "line-opacity": 0.82 } },
    { id: "lyr-blm", type: "line", source: "src-blm",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ["case", ["to-boolean", ["get", "motorized"]], "#dc2626", "#7c3aed"], "line-width": 2.75, "line-opacity": 0.86 } },
    { id: "lyr-trails", type: "line", source: "src-trails",
      filter: ["!=", ["geometry-type"], "Point"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#ff6a00", "line-width": 4, "line-opacity": 0.95 } },
    { id: "lyr-trails-selected", type: "line", source: "src-trails-selected",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#ff3d00", "line-width": 6 } },
    { id: "lyr-trails-pt", type: "circle", source: "src-trails",
      filter: ["==", ["geometry-type"], "Point"],
      paint: { "circle-radius": 6, "circle-color": "#2e7d32", "circle-stroke-color": "#fff", "circle-stroke-width": 2 } },
    { id: "lyr-rivers-pt", type: "circle", source: "src-rivers",
      filter: ["==", ["geometry-type"], "Point"],
      paint: { "circle-radius": 6, "circle-color": "#0277bd", "circle-stroke-color": "#fff", "circle-stroke-width": 2 } }
  ]
}

export function vectorLayerKeyForStyleLayer(styleLayerId) {
  return Object.keys(VECTOR_LAYERS).find(key => VECTOR_LAYERS[key].includes(styleLayerId))
}

export function layerLoadPlan(key, data = {}) {
  if (!data.ok) return { ok: false, message: data.error || data.message, featureCount: 0 }

  const features = featuresWithIds(data.geojson?.features || [])
  if (MANAGED_TRAIL_LAYERS.includes(key)) {
    return { ok: true, kind: "managedTrail", features, featureCount: features.length, message: data.message }
  }

  if (key === "rivers") {
    return {
      ok: true,
      kind: "geojsonSource",
      sourceId: "src-rivers",
      featureDataKey: "rivers",
      features,
      featureCount: features.length,
      message: data.message
    }
  }

  if (key === "parcels") {
    return {
      ok: true,
      kind: "geojsonSource",
      sourceId: "src-parcels",
      featureDataKey: "parcels",
      features,
      featureCount: features.length,
      labelFeatures: features.length <= PARCEL_LABEL_LIMIT,
      message: data.message
    }
  }

  if (key === "public_lands") {
    return {
      ok: true,
      kind: "geojsonSource",
      sourceId: "src-public-lands",
      featureDataKey: "public_lands",
      features,
      featureCount: features.length,
      message: data.message
    }
  }

  if (key === "wma") {
    return {
      ok: true,
      kind: "geojsonSource",
      sourceId: "src-wma",
      featureDataKey: "wma",
      features,
      featureCount: features.length,
      message: data.message
    }
  }

  const markers = data.markers || []
  return { ok: true, kind: "markers", markers, featureCount: markers.length, message: data.message }
}

export function clearLayerPlan(key) {
  const sourceIds = []
  if (key === "trails") sourceIds.push("src-trails")
  if (key === "rivers") sourceIds.push("src-rivers")
  if (MANAGED_TRAIL_LAYERS.includes(key)) sourceIds.push(MANAGED_TRAIL_SOURCES[key])
  if (key === "wma") sourceIds.push("src-wma")
  if (key === "public_lands") sourceIds.push("src-public-lands")
  if (key === "parcels") sourceIds.push("src-parcels")

  return {
    clearTrailSelection: key === "trails",
    clearManagedTrailFeatures: MANAGED_TRAIL_LAYERS.includes(key),
    sourceIds: Array.from(new Set(sourceIds.filter(Boolean)))
  }
}

export function featuresWithIds(features = []) {
  return Array.from(features || []).map((feature, index) => ({ ...feature, id: index }))
}

// Label anchor for a parcel: the average of the largest outer ring's points —
// cheap, and close enough to the old Leaflet tooltip "center" placement.
export function polygonLabelPoint(geometry) {
  let ring = null
  if (geometry?.type === "Polygon") ring = geometry.coordinates[0]
  if (geometry?.type === "MultiPolygon") {
    ring = geometry.coordinates.map(p => p[0]).sort((a, b) => b.length - a.length)[0]
  }
  if (!ring?.length) return null
  const [sx, sy] = ring.reduce(([ax, ay], [x, y]) => [ax + x, ay + y], [0, 0])
  return [sx / ring.length, sy / ring.length]
}
